/**
 * LUNA Coding Tutor - Monaco Code Editor Component
 * 
 * Monaco Editor (VS Code 엔진) 기반 코드 에디터
 * IME (Korean/Japanese) 입력 문제 해결을 위해 CodeMirror에서 마이그레이션
 */

import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import Editor, { loader } from '@monaco-editor/react';
import clsx from 'clsx';

// ============================================
// Monaco Loader 설정
// ============================================

// [Fix] Monaco의 AMD 로더가 전역 require/define을 덮어써서 발생하는 
// 다른 라이브러리(Babel, Vite 등)의 stackframe.js 로드 실패 오류를 해결하기 위해
// 로더를 명시적으로 설정합니다.
loader.config({
    paths: {
        vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.45.0/min/vs'
    }
});

// ============================================
// 유틸리티 함수
// ============================================

/**
 * 코드 문자열 스마트 포매팅 (다국어 지원)
 * 
 * \\n, \\t 변환 규칙:
 * - 주석/문자열 밖: \\n → 실제 줄바꿈, \\t → 실제 탭
 * - 주석/문자열 안: \\n, \\t 그대로 유지 (리터럴 표시)
 * 
 * @param {string} text - 원본 코드
 * @param {string} language - 언어 (python, javascript, html, vba 등)
 */
function formatCodeString(text, language = 'python') {
    if (!text) return '';

    const str = String(text);
    const lang = (language || 'python').toLowerCase();
    let result = '';
    let inString = false;
    let inLineComment = false;
    let inBlockComment = false;
    let blockCommentEnd = '';
    let stringChar = '';
    let i = 0;

    while (i < str.length) {
        const char = str[i];
        const nextChar = str[i + 1] || '';
        const next2 = str.substring(i, i + 2);
        const next3 = str.substring(i, i + 3);
        const next4 = str.substring(i, i + 4);

        // 블록 주석 안에서
        if (inBlockComment) {
            // 블록 주석 종료 체크
            if (str.substring(i, i + blockCommentEnd.length) === blockCommentEnd) {
                result += blockCommentEnd;
                i += blockCommentEnd.length;
                inBlockComment = false;
                blockCommentEnd = '';
                continue;
            }
            // 블록 주석 안의 \\n, \\t는 그대로 유지
            if (next2 === '\\n') {
                result += '\\n';
                i += 2;
                continue;
            }
            if (next2 === '\\t') {
                result += '\\t';
                i += 2;
                continue;
            }
            result += char;
            i++;
            continue;
        }

        // 이스케이프된 따옴표 (\", \')
        if (char === '\\' && (nextChar === '"' || nextChar === "'")) {
            result += char + nextChar;
            i += 2;
            continue;
        }

        // 블록 주석 시작
        if (!inString && !inLineComment) {
            if (next2 === '/*' && ['javascript', 'js', 'jsx', 'css'].includes(lang)) {
                inBlockComment = true;
                blockCommentEnd = '*/';
                result += next2;
                i += 2;
                continue;
            }
            if (next4 === '<!--' && ['html', 'htmlmixed', 'xml'].includes(lang)) {
                inBlockComment = true;
                blockCommentEnd = '-->';
                result += next4;
                i += 4;
                continue;
            }
        }

        // 한줄 주석 시작
        if (!inString && !inLineComment && !inBlockComment) {
            if (char === '#' && ['python', 'py', 'r', 'bash', 'shell'].includes(lang)) {
                inLineComment = true;
                result += char;
                i++;
                continue;
            }
            if (next2 === '//' && ['javascript', 'js', 'jsx', 'c', 'cpp', 'java'].includes(lang)) {
                inLineComment = true;
                result += next2;
                i += 2;
                continue;
            }
            if (char === "'" && ['vba', 'bas', 'excel'].includes(lang)) {
                inLineComment = true;
                result += char;
                i++;
                continue;
            }
        }

        // 문자열 시작/종료 (주석 안에서도 추적하여 예시 문자열 감지)
        if (!inBlockComment) {
            if ((char === '"' || char === "'") && !inString) {
                inString = true;
                stringChar = char;
                result += char;
                i++;
                continue;
            }
            if (char === stringChar && inString) {
                inString = false;
                stringChar = '';
                result += char;
                i++;
                continue;
            }
        }

        // \\n 처리 - 핵심 규칙:
        // 1. 문자열 안: 리터럴 유지 (코드 또는 주석 내 예시 모두)
        // 2. 주석 안 + 문자열 밖: 줄바꿈으로 변환 (다음 주석 줄로)
        // 3. 코드 영역: 줄바꿈으로 변환
        if (next2 === '\\n') {
            if (inString) {
                // 문자열 안: 무조건 리터럴 유지 (예: # 예: "Hi\nBye" 또는 s = "a\nb")
                result += '\\n';
            } else {
                // 문자열 밖: 줄바꿈으로 변환 (주석 안이든 밖이든)
                result += '\n';
                inLineComment = false; // 줄바꿈 시 한줄 주석 종료
            }
            i += 2;
            continue;
        }

        // \\t 처리
        if (next2 === '\\t') {
            if (inString || inLineComment || inBlockComment) {
                result += '\\t';
            } else {
                result += '\t';
            }
            i += 2;
            continue;
        }

        result += char;
        i++;
    }

    return result;
}

function getLanguage(language) {
    const langMap = {
        'python': 'python',
        'javascript': 'javascript',
        'js': 'javascript',
        'jsx': 'javascript', // [Fix] Map jsx to javascript for Monaco
        'html': 'html',
        'htmlmixed': 'html',
        'css': 'css',
        'bash': 'shell',
        'shell': 'shell',
    };
    return langMap[language?.toLowerCase()] || 'python';
}

function getFilename(language) {
    const filenameMap = {
        'python': 'main.py',
        'javascript': 'main.js',
        'jsx': 'App.jsx', // [Fix] Add App.jsx for JSX
        'html': 'index.html',
        'css': 'style.css',
        'shell': 'script.sh',
    };
    return filenameMap[language] || 'main.py'; // Fallback logic is risky if language is normalized
}

// ============================================
// Monaco 테마 정의 (다크 모드)
// ============================================

const customTheme = {
    base: 'vs-dark',
    inherit: true,
    rules: [
        { token: 'comment', foreground: '6A9955' },
        { token: 'keyword', foreground: 'C586C0' },
        { token: 'string', foreground: 'CE9178' },
        { token: 'number', foreground: 'B5CEA8' },
        { token: 'tag', foreground: '569CD6' },
        { token: 'attribute.name', foreground: '9CDCFE' },
        { token: 'attribute.value', foreground: 'CE9178' },
    ],
    colors: {
        'editor.background': '#00000000', // Transparent
        'editor.foreground': '#e2e8f0',
        'editor.lineHighlightBackground': '#ffffff05', // Very subtle highlight
        'editorLineNumber.foreground': '#475569',
        'editorLineNumber.activeForeground': '#94a3b8',
        'editor.selectionBackground': '#ffffff10',
        'editorCursor.foreground': '#22d3ee',
    }
};

// ============================================
// 메인 컴포넌트
// ============================================

const MonacoCodeEditor = forwardRef(function MonacoCodeEditor({
    initialCode = '',
    language = 'python',
    readOnly = false,
    placeholder = '// 코드를 작성해주세요',
    showLineNumbers = true,
    height = '300px',
    onChange,
    onRun,
    className,
    hideHeader = false, // [New] 헤더 숨김 옵션
}, ref) {
    const [code, setCode] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const editorRef = useRef(null);
    const monacoRef = useRef(null);

    const monacoLanguage = useMemo(() => getLanguage(language), [language]);
    // [Fix] Use original language prop for filename to preserve 'jsx' -> 'App.jsx' mapping
    const filename = useMemo(() => getFilename(language), [language]);

    // 초기 코드 설정
    useEffect(() => {
        const formattedCode = formatCodeString(initialCode, language);
        setCode(formattedCode);

        // 에디터가 이미 마운트된 경우 직접 값 설정
        if (editorRef.current) {
            const currentValue = editorRef.current.getValue();
            if (currentValue !== formattedCode) {
                editorRef.current.setValue(formattedCode);
            }
        }
    }, [initialCode]);

    // Ref를 통한 외부 접근 API
    useImperativeHandle(ref, () => ({
        getCode: () => code,
        setCode: (newCode) => {
            const formatted = formatCodeString(newCode, language);
            setCode(formatted);
            if (editorRef.current) {
                editorRef.current.setValue(formatted);
            }
        },
        focus: () => editorRef.current?.focus(),
        getEditorInstance: () => editorRef.current,
    }));

    // Monaco Editor 마운트 시 콜백
    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;
        setIsLoading(false);

        // 커스텀 테마 등록
        monaco.editor.defineTheme('luna-dark', customTheme);
        monaco.editor.setTheme('luna-dark');

        // [Fix] AMD 로더 충돌 방지를 위해 전역 require가 Monaco 것인 경우 보호 조치
        // (필요 시 추가적인 로직을 여기에 작성할 수 있습니다)

        // 초기 코드 설정
        const formattedCode = formatCodeString(initialCode, language);
        if (formattedCode && editor.getValue() !== formattedCode) {
            editor.setValue(formattedCode);
        }

        console.log('[MonacoEditor] Editor mounted successfully');
    };

    // 코드 변경 핸들러
    const handleChange = (value) => {
        setCode(value || '');
        if (onChange) {
            onChange(value || '');
        }
    };

    // 실행 단축키
    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            if (onRun) {
                onRun(code);
            }
        }
    };

    return (
        <div
            className={clsx(
                'code-editor-container rounded-xl overflow-hidden border border-white/5 bg-black/20 backdrop-blur-md flex flex-col',
                className
            )}
            onKeyDown={handleKeyDown}
            style={height === '100%' ? { height: '100%' } : {}} // Ensure container takes full height if prop is 100%
        >
            {/* IDE 스타일 헤더 */}
            {!hideHeader && (
                <div className="ide-header flex items-center justify-between px-4 py-2 bg-white/5 border-b border-white/5 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="ide-dots flex gap-1.5">
                            <span className="w-3 h-3 rounded-full bg-red-500" />
                            <span className="w-3 h-3 rounded-full bg-yellow-500" />
                            <span className="w-3 h-3 rounded-full bg-green-500" />
                        </div>
                        <span className="text-xs text-slate-400">{filename}</span>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 bg-slate-700/50 px-2 py-1 rounded">
                            {monacoLanguage}
                        </span>
                        {onRun && (
                            <button
                                onClick={() => onRun(code)}
                                className="flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                            >
                                <i className="fas fa-play text-[10px]" />
                                <span>실행</span>
                                <span className="text-green-300/70 text-[10px]">Ctrl+↵</span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Monaco Editor */}
            <div
                className={clsx('relative', height === '100%' ? 'flex-1 min-h-0' : '')}
                style={height === '100%' ? {} : { height }}
            >
                {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent text-slate-400">
                        <i className="fas fa-spinner fa-spin mr-2" />
                        에디터 로딩 중...
                    </div>
                )}
                <Editor
                    height="100%"
                    language={monacoLanguage}
                    value={code}
                    onChange={handleChange}
                    onMount={handleEditorDidMount}
                    theme="vs-dark"
                    options={{
                        readOnly: readOnly,
                        minimap: { enabled: false },
                        lineNumbers: showLineNumbers ? 'on' : 'off',
                        fontSize: 14,
                        fontFamily: "'Fira Code', 'Consolas', monospace",
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        automaticLayout: true,
                        tabSize: 2,
                        padding: { top: 12, bottom: 12 },
                        renderLineHighlight: 'line',
                        cursorBlinking: 'smooth',
                        smoothScrolling: true,
                        // 자동 완성 및 제안 비활성화 (학습용)
                        quickSuggestions: false,
                        suggestOnTriggerCharacters: false,
                        acceptSuggestionOnEnter: 'off',
                        wordBasedSuggestions: 'off',
                        // HTML 자동 닫힘 태그 비활성화 (IME 호환성)
                        autoClosingBrackets: 'never',
                        autoClosingQuotes: 'never',
                    }}
                />
            </div>
        </div>
    );
});

export default MonacoCodeEditor;
